export const CONTRACT_EXPIRATION_WARNING_MONTHS = 3;

function parseContractDate(value: string | Date): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]) - 1;
    const day = Number(dateOnlyMatch[3]);
    const date = new Date(year, month, day);

    return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day
      ? date
      : null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function addMonthsClamped(value: Date, months: number): Date {
  const firstDayOfTargetMonth = new Date(value.getFullYear(), value.getMonth() + months, 1);
  const lastDayOfTargetMonth = new Date(
    firstDayOfTargetMonth.getFullYear(),
    firstDayOfTargetMonth.getMonth() + 1,
    0
  ).getDate();

  return new Date(
    firstDayOfTargetMonth.getFullYear(),
    firstDayOfTargetMonth.getMonth(),
    Math.min(value.getDate(), lastDayOfTargetMonth)
  );
}

export function normalizeOptionalBoolean(value: unknown): boolean | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return ['true', '1'].includes(value.toLowerCase());
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return Boolean(value);
}

export function shouldWarnContractExpiration(
  contractEndDate: string | null | undefined,
  automaticRenewal: boolean | null | undefined,
  referenceDate: string | Date = new Date()
): boolean {
  if (automaticRenewal !== false || !contractEndDate) {
    return false;
  }

  const parsedEndDate = parseContractDate(contractEndDate);
  const parsedReferenceDate = parseContractDate(referenceDate);

  if (!parsedEndDate || !parsedReferenceDate) {
    return false;
  }

  const endDate = startOfDay(parsedEndDate);
  const warningLimit = addMonthsClamped(startOfDay(parsedReferenceDate), CONTRACT_EXPIRATION_WARNING_MONTHS);
  return endDate.getTime() <= warningLimit.getTime();
}

export function formatContractDate(contractDate: string | null | undefined): string {
  if (!contractDate) {
    return '-';
  }

  const date = parseContractDate(contractDate);
  if (!date) {
    return '-';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}
